import { ConfigurableProduct, MediaGalleryInterface } from '@magentoTypes'
import { Image } from '~/elements/Image/Image'
import clsx from 'clsx'
import { DialogOverlay, DialogContent } from '@reach/dialog'
import { SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { Icon } from '~/elements/Icons/Icon'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Lazy, Pagination } from 'swiper'
import { AugmentedRealityPopup } from './AugmentedRealityPopup'
import { useBrandType } from '~/hooks/useBrandType'
import { useMedia } from '~/hooks/useMedia'
import { useRebates } from '~/hooks/useRebates'
import AugmentedRealityBlock from './AugmentedRealityBlock'
import { useProductAttributes } from '~/hooks/useProductAttributes'

SwiperCore.use([Pagination, Lazy])

type Props = {
  gallery: [MediaGalleryInterface]
  className?: string
  product: ConfigurableProduct
  productPrice?: ConfigurableProduct
}

export const Gallery = (props: Props) => {
  const isDesktop = useMedia('lg')
  const { gallery, className } = props
  const router = useRouter()
  const [modalImage, setModalImage] = useState<MediaGalleryInterface | false>(false)
  const [showFullsize, setShowFullsize] = useState(false)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [isHydrated, setIsHydrated] = useState(false)
  const { t } = useTranslation()
  const { calculateRebates } = useRebates()
  const { findLabel } = useProductAttributes()
  const sortedGallery = gallery?.sort((a, b) => (a?.position || 0) - (b?.position || 0))

  const getDiscountAmount = () => {
    return calculateRebates(props?.productPrice ? props?.productPrice : props?.product)
  }

  const hasBestseller = (props.product?.umsatz || -1) >= 0 && (props.product?.umsatz || -1) <= 35
  const isNewProduct = (created_at: string): boolean => {
    const createdAtDate = new Date(created_at)
    const currentDate = new Date()
    const timeDifference = currentDate.getTime() - createdAtDate.getTime()
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24)
    return daysDifference <= 180
  }

  const handleOpenModal = (image: MediaGalleryInterface) => {
    setModalImage(image)
  }

  const handleCloseModal = () => {
    setModalImage(false)
  }

  const toggleFullsize = () => {
    setShowFullsize((prev) => !prev)
  }

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    setDimensions({
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    })
  }

  const [showMobileARPopup, setShowMobileARPopup] = useState(false)
  const closeMobileARPopup = () => setShowMobileARPopup(false)

  const [showDesktopARPopup, setShowDesktopARPopup] = useState(false)
  const closeDesktopARPopup = () => setShowDesktopARPopup(false)

  const brandType = useBrandType({ manufacturer: props.product?.manufacturer as number })

  const generateAltTags = (product: ConfigurableProduct) => {
    const isRugProduct = product?.attribute_set_id === 115
    return isRugProduct
      ? t(
          'Rug made of {{Placeholder_Material}} in {{Placeholder_Colour}} with a {{Placeholder_Pile_Height}} high pile by {{Placeholder_Subbrand}}',
          {
            Placeholder_Material: findLabel({
              attributeCode: 'benuta_pile_material',
              attributeValue: product?.benuta_pile_material as number,
            }),
            Placeholder_Colour: findLabel({
              attributeCode: 'benuta_color_filter',
              attributeValue: product?.benuta_color_filter as number,
            }),
            Placeholder_Pile_Height: findLabel({
              attributeCode: 'benuta_pile_height',
              attributeValue: product?.benuta_pile_height as number,
            }),
            Placeholder_Subbrand: findLabel({
              attributeCode: 'manufacturer',
              attributeValue: product?.manufacturer as number,
            }),
          },
        )
      : t(
          '{{Placeholder_Product_Name}} in {{Placeholder_First_Element_of_Design}} design made of {{Placeholder_Material}} by {{Placeholder_Subbrand}}',
          {
            Placeholder_Product_Name: product?.name || '',
            Placeholder_Colour: findLabel({
              attributeCode: 'benuta_color_filter',
              attributeValue: product?.benuta_color_filter as number,
            }),
            Placeholder_First_Element_of_Design: findLabel({
              attributeCode: 'benuta_motive_design',
              attributeValue: product?.benuta_motive_design as string,
            })
              .replace(/\s/g, '')
              .split(',')?.[0],
            Placeholder_Material: findLabel({
              attributeCode: 'benuta_pile_material',
              attributeValue: product?.benuta_pile_material as number,
            }),
            Placeholder_Subbrand: findLabel({
              attributeCode: 'manufacturer',
              attributeValue: product?.manufacturer as number,
            }),
          },
        )
  }

  useEffect(() => {
    if (!isHydrated && props?.productPrice) setIsHydrated(true)
  }, [props?.productPrice, isHydrated])

  return (
    <>
      <div className="block lg:hidden">
        {!isDesktop && (
          <>
            <div className="relative w-full">
              <Swiper
                pagination={{
                  clickable: true,
                  renderBullet: function (index, className) {
                    return '<span class="' + className + '"></span>'
                  },
                }}
                lazy={true}
              >
                {sortedGallery?.map((image, index) =>
                  image?.url ? (
                    <SwiperSlide key={`${image?.url}-${index}`}>
                      <Image
                        className="h-[500px] w-full"
                        src={image?.url}
                        alt={generateAltTags(props.product)}
                        width={759}
                        height={1000}
                        style={{ objectFit: 'cover', objectPosition: 'center' }}
                        sizes="(max-width: 1024px) 100vw, 0vw"
                        loading={index === 0 ? 'eager' : 'lazy'}
                        onClick={() => {
                          handleOpenModal(image)
                        }}
                      />
                    </SwiperSlide>
                  ) : null,
                )}
              </Swiper>
              <DialogOverlay
                style={{
                  width: '100vw',
                  height: '100vh',
                  top: 0,
                  left: 0,
                  zIndex: 9999,
                  padding: 0,
                  margin: 0,
                }}
                isOpen={!!modalImage}
                onDismiss={handleCloseModal}
                allowPinchZoom={true}
              >
                <DialogContent
                  style={{
                    position: 'relative',
                    width: '100vw',
                    height: '100vh',
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <div className="block lg:hidden">
                    <>
                      <button
                        className="fixed top-4 right-4 z-50 grid h-10 w-10 place-items-center rounded-full bg-grey-light"
                        onClick={handleCloseModal}
                      >
                        <Icon name="cross" />
                      </button>

                      <div onClick={handleCloseModal} className="vh100 relative w-screen">
                        <Image
                          src={(modalImage as MediaGalleryInterface)?.url || ''}
                          alt={`${generateAltTags(props.product)}`}
                          fill
                          style={{ objectFit: 'contain' }}
                          sizes="(max-width: 1024px) 200vw, 0vw"
                          onLoad={handleImageLoad}
                        />
                      </div>
                    </>
                  </div>
                </DialogContent>
              </DialogOverlay>
              <div className="absolute top-8 right-5 z-20">
                <div className="flex w-full items-center justify-center gap-x-3">
                  {props?.product?.benuta_ar && (
                    <button
                      className="h-10 w-10 rounded-full bg-primary-white"
                      onClick={() => setShowMobileARPopup(true)}
                    >
                      <div className="flex h-full w-full items-center justify-center">
                        <Icon name="augmented-reality-cube" setWidth={22} setHeight={22} />
                      </div>
                    </button>
                  )}
                  {props?.product?.video_url ? (
                    <a
                      href="#product-video"
                      title="jump to product video"
                      onClick={() => router.replace('#product-video')}
                    >
                      <Icon name="video-play" className="h-10 w-10" />
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="absolute top-8 right-0 z-10 mt-[385px]">
                {brandType && (
                  <Image
                    src={brandType.svg}
                    width={85}
                    height={38}
                    placeholder="empty"
                    alt={brandType.name}
                  />
                )}
              </div>
              <div className="absolute  top-8 left-0 z-10 w-full">
                {/*isHydrated && getDiscountAmount() > 0 && (
                      <div className="flex w-fit items-center justify-center bg-[#F3D750] px-2 text-p-small font-bold text-primary-black">
                        {t('up to -{{discount}}%', {
                          discount: getDiscountAmount(),
                        })}
                      </div>
                    )*/}
                {hasBestseller && (
                  <div className="mt-1 flex w-fit items-center justify-center bg-[#96A9AB] px-2 text-p-small text-primary-white">
                    {t('bestseller')}
                  </div>
                )}
                {isNewProduct(props.product?.created_at as string) && (
                  <div className="mt-1 flex  w-fit items-center  justify-center bg-[#000] px-2 text-p-small text-primary-white">
                    {t('new')}
                  </div>
                )}
              </div>
            </div>
            {props?.product?.benuta_ar && (
              <AugmentedRealityPopup
                sku={props?.product?.sku as string}
                uuid={props?.product?.benuta_ar}
                open={showMobileARPopup}
                close={closeMobileARPopup}
              />
            )}
          </>
        )}
      </div>
      <div className="hidden lg:block">
        <div className={clsx(className)}>
          <ul className={clsx('grid grid-cols-2 gap-2')}>
            {sortedGallery?.map((entry, index) => (
              <li
                className="relative aspect-[2/3]"
                key={`${entry?.url}-${index}`}
                style={{ cursor: `url("/images/Icons/zoom-in.svg"), zoom-in` }}
              >
                <div className="h-full w-full" />
                {isDesktop && (
                  <Image
                    src={entry?.url as string}
                    alt={generateAltTags(props.product)}
                    fill
                    style={{
                      objectFit: index === 0 || index === 1 ? 'contain' : 'cover',
                      objectPosition: index === 0 || index === 1 ? 'top' : 'center',
                    }}
                    loading={index > 1 ? 'lazy' : 'eager'}
                    onClick={() => handleOpenModal(gallery[index])}
                    className="z-20"
                    sizes="(max-width: 1024px) 100vw, 34vw"
                  />
                )}
                {index === 0 && (
                  <div className="absolute top-10 left-10 z-20 h-[28px] min-w-[91px]">
                    {/*isHydrated && getDiscountAmount() > 0 && (
                                      <div className="flex h-full w-full items-center justify-center bg-[#F3D750] px-2 text-[14px] font-bold text-primary-black">
                                        {t('up to -{{discount}}%', {
                                          discount: getDiscountAmount(),
                                        })}
                                      </div>
                                    )*/}
                    {hasBestseller && (
                      <div className="mt-1 flex h-full w-fit items-center justify-center bg-[#96A9AB] px-2 text-[14px] text-primary-white lg:px-4">
                        {t('bestseller')}
                      </div>
                    )}
                    {isNewProduct(props.product?.created_at as string) && (
                      <div className="mt-1 flex h-full w-full items-center justify-center bg-[#000] px-2 text-[14px] text-primary-white">
                        {t('new')}
                      </div>
                    )}
                  </div>
                )}
                {index === 1 && (
                  <>
                    <div className="absolute top-10 right-10 z-20">
                      <div className="flex w-full items-center justify-center gap-x-4">
                        {props?.product?.benuta_ar && (
                          <button
                            className="rounded-full bg-primary-white p-2"
                            onClick={() => setShowDesktopARPopup(true)}
                          >
                            <div className="align-center flex px-2.5">
                              <Icon name="augmented-reality-cube" setWidth={22} setHeight={22} />
                              <span className="ml-2.5 text-[14px] font-medium text-[#141414]">
                                {t('Show in room')}
                              </span>
                            </div>
                          </button>
                        )}
                        {props?.product?.video_url ? (
                          <a
                            href="#product-video"
                            title="jump to product video"
                            onClick={() => router.replace('#product-video')}
                          >
                            <Icon name="video-play" className="h-10 w-10" />
                          </a>
                        ) : null}
                      </div>
                    </div>
                    <div className="absolute top-3/4 right-0 z-20">
                      {brandType && (
                        <div className="-mr-1">
                          <Image
                            src={brandType.svg}
                            width={85}
                            height={38}
                            placeholder="empty"
                            alt={brandType.name}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
          {isDesktop && (
            <DialogOverlay
              style={{
                height: '100vh',
                width: '100vw',
                display: 'grid',
                placeItems: 'center',
                zIndex: 9999,
              }}
              isOpen={!!modalImage}
              onDismiss={handleCloseModal}
              aria-hidden={!!!modalImage}
            >
              <DialogContent
                aria-labelledby="Image gallery"
                style={{ height: '90vh', width: '94vw', margin: 0, position: 'relative' }}
              >
                <>
                  <button
                    className="absolute top-4 right-4 z-10 grid h-10 w-10 place-content-center rounded-full bg-grey-light transition-transform hover:scale-125"
                    onClick={handleCloseModal}
                  >
                    <Icon name="cross" />
                  </button>
                  <div
                    className={clsx(
                      'relative grid h-full w-full overflow-auto p-4',
                      showFullsize ? 'grid-cols-1' : 'grid-cols-[98px,1fr]',
                    )}
                  >
                    {showFullsize ? null : (
                      <ul className="flex flex-col self-center">
                        {sortedGallery?.map((image, i) =>
                          image?.url ? (
                            <li key={`${image.url}-${i}`} className="mb-4 h-[130px]">
                              <button
                                onClick={() => setModalImage(image)}
                                className={clsx(
                                  'relative h-full w-full border',
                                  modalImage && image.url === modalImage.url
                                    ? 'border-red'
                                    : 'border-gray',
                                )}
                              >
                                <Image
                                  src={image.url}
                                  alt={`${generateAltTags(props.product)}`}
                                  width={96}
                                  height={128}
                                  hasNoBlur
                                  className="test"
                                />
                              </button>
                            </li>
                          ) : null,
                        )}
                      </ul>
                    )}
                    {modalImage && modalImage?.url?.length ? (
                      <button
                        onClick={toggleFullsize}
                        className={clsx('relative')}
                        style={{
                          cursor: showFullsize
                            ? `url("/images/Icons/zoom-out.svg"), zoom-out`
                            : `url("/images/Icons/zoom-in.svg"), zoom-in`,
                        }}
                      >
                        {modalImage ? (
                          showFullsize ? (
                            <div
                              style={{
                                width: '90vw',
                                height: `calc(90vw * (${dimensions.height} / ${dimensions.width}))`,
                                position: 'relative',
                              }}
                            >
                              <Image
                                src={modalImage.url}
                                alt={`${generateAltTags(props.product)}`}
                                fill
                                sizes="90vw"
                                style={{ objectFit: 'cover' }}
                                onLoad={handleImageLoad}
                              />
                            </div>
                          ) : (
                            <Image
                              src={modalImage.url}
                              alt={`${generateAltTags(props.product)}`}
                              fill
                              style={{ objectFit: 'contain' }}
                              sizes="85vw"
                            />
                          )
                        ) : null}
                      </button>
                    ) : null}
                  </div>
                </>
              </DialogContent>
            </DialogOverlay>
          )}
          <AugmentedRealityBlock
            sku={props?.product?.sku as string}
            uuid={props?.product?.benuta_ar as string}
            open={showDesktopARPopup}
            close={closeDesktopARPopup}
          />
        </div>
      </div>
    </>
  )
}
